<template>
  <Grid
    class="workflow-grid"
    :pageable="controller.pageable.value"
    :data-items="workflows"
    :skip="controller.skip.value"
    :take="controller.take.value"
    :total="total"
    :columns="controller.columns.value"
    :loader="loading"
    :filter="filter"
    :column-menu="!loading"
    :sort="sort"
    @columnreorder="onColumnReorder"
    @columnresize="controller.onColumnResize($event)"
    @filterchange="controller.filterChangeHandler($event)"
    @sortchange="controller.sortChangeHandler($event)"
    @pagechange="controller.pageChangeHandler($event)"
    data-cy="workflow-grid"
  >
    <template v-slot:columnMenu="{ props }">
      <ColumnMenu
        :column="props.column"
        :filterable="props.filterable"
        :filter="props.filter"
        :sortable="props.sortable"
        :sort="props.sort"
        :columns="controller.columns.value"
        :getItems="getPropValues"
        @sortchange="(e: any) => props.onSortchange(e)"
        @filterchange="(e: any) => props.onFilterchange(e)"
        @closemenu="(e: any) => props.onClosemenu(e)"
        @contentfocus="(e: any) => props.onContentfocus(e)"
        @columnssubmit="onColumnsSubmit"
      />
    </template>
    <template v-slot:UrgentHeaderCell>
      <th class="k-header">
        <input type="image" :src="urgentHeaderIcon" />
      </th>
    </template>
    <template v-slot:PrivateNotesHeaderCell>
      <th class="k-header">
        <input type="image" :src="privateNotesHeaderIcon" />
      </th>
    </template>
    <template v-slot:UrgentCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <img
          class="urgent-icon"
          :src="dataItem.IsUrgent ? urgentIcon : notUrgentIcon"
          @click="onUrgentClick(dataItem)"
          alt="Click to mark urgent"
          data-cy="urgent-action"
        />
      </td>
    </template>
    <template v-slot:PrivateNotesCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <img
          class="private-notes-icon"
          :src="
            dataItem.LatestUserComment
              ? privateNotesIconActive
              : privateNotesIcon
          "
          @click="onNotesClick(dataItem)"
          alt="Click to add private notes"
          data-cy="private-notes-action"
        />
      </td>
    </template>
    <template v-slot:JobIdCell="{ props: { dataItem } }">
      <td class="k-table-td">
        <a
          :href="buildUrl(dataItem)"
          target=""
          rel="noopener noreferrer"
          data-cy="job-id-link"
        >
          {{ dataItem.JobId }}
        </a>
      </td>
    </template>
    <template v-slot:JobTypeCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <span>{{
          enumHelper.splitByCapitalLetter(EnumSubmissionType[dataItem[field]])
        }}</span>
      </td>
    </template>

    <template v-slot:StatusCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        <span>{{
          enumHelper.splitByCapitalLetter(EnumWorkflowStatus[dataItem[field]])
        }}</span>
      </td>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import urgentHeaderIcon from "@/assets/img/error-icon-15.png";
import privateNotesHeaderIcon from "@/assets/img/icon-edit.png";
import urgentIcon from "@/assets/img/Urgent1Red.png";
import notUrgentIcon from "@/assets/img/Urgent1Red_na.png";
import privateNotesIcon from "@/assets/img/pencil_na.png";
import privateNotesIconActive from "@/assets/img/pencil.png";
import enumHelper from "@/shared/helpers/enumHelper";
import { watchEffect } from "vue";
import ColumnMenu from "@/shared/ui/inputs/ColumnMenu.vue";
import {
  getFilterOperatorsForDate,
  getFilterOperatorsForEnum,
  getFilterOperatorsForNumber,
} from "@/shared/helpers/filtersHelper";
import type {
  CompositeFilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import type { WfBase } from "@/Domain/WfBase";
import GridController from "@/shared/services/GridController";
import {
  Grid,
  type GridColumnProps,
  type GridColumnReorderEvent,
} from "@progress/kendo-vue-grid";
import type { WfSubmissionProductAndIngredient } from "@/Domain/WfSubmissionProductAndIngredient";
import { useMainStore } from "@/stores/useMainStore";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { api } from "@/api";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { EnumSubmissionType } from "@/Domain/EnumSubmissionType";
import { EnumWorkflowStatus } from "@/Domain/EnumWorkflowStatus";

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  workflows: WfSubmissionProductAndIngredient[];
  loading: boolean;
  skip: number;
  pageSize: number;
  total: number;
  filter: CompositeFilterDescriptor;
  sort: SortDescriptor[];
  getPropValues: (
    column: GridColumnPropsExtended
  ) => Promise<WfSubmissionProductAndIngredient[]>;
}>();

const emit = defineEmits<{
  (e: "itemsChanged", options: ItemChangedOptions): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
  (e: "privateNotesClicked", wf: WfSubmissionProductAndIngredient): void;
  (e: "columnreorder", event: GridColumnReorderEvent): void;
  (e: "columnssubmit", columnsProps: GridColumnProps[]): void;
}>();

const columns = [
  {
    cell: "UrgentCell",
    headerCell: "UrgentHeaderCell",
    headerClassName: "hide-menu",
    sortable: false,
    filterable: false,
    width: "40px",
  },
  {
    cell: "PrivateNotesCell",
    headerCell: "PrivateNotesHeaderCell",
    headerClassName: "hide-menu",
    sortable: false,
    filterable: false,
    width: "40px",
  },
  {
    cell: "JobIdCell",
    title: "Job ID",
    field: "JobId",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForNumber(),
  },
  {
    title: "Request Type",
    field: "JobType",
    cell: "JobTypeCell",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForEnum(),
  },
  {
    title: "Date Submitted",
    field: "SubmissionDate",
    columnMenu: "columnMenu",
    type: "date",
    format: "{0:MM/dd/yyyy h:mm a}",
    allowedFilterOperators: getFilterOperatorsForDate(),
  },
  {
    title: "Company Name",
    field: "CompanyName",
    columnMenu: "columnMenu",
  },
  {
    title: "Company ID",
    field: "CompanyId",
    columnMenu: "columnMenu",
    allowedFilterOperators: getFilterOperatorsForNumber(),
  },
  {
    title: "RC",
    field: "CompanyRc",
    columnMenu: "columnMenu",
  },
  {
    title: "Status",
    field: "Status",
    cell: "StatusCell",
    columnMenu: "columnMenu",
  },
  {
    title: "Assigned To",
    field: "CurrentlyAssignedToRfr",
    columnMenu: "columnMenu",
  },
  {
    title: "Latest Comment",
    field: "LatestComment",
    columnMenu: "columnMenu",
  },
  {
    title: "Date Modified",
    field: "LastModified",
    columnMenu: "columnMenu",
    type: "date",
    format: "{0:MM/dd/yyyy h:mm a}",
    allowedFilterOperators: getFilterOperatorsForDate(),
  },
  {
    title: "Modified By",
    field: "LastModifiedByRfr",
    columnMenu: "columnMenu",
  },
] as GridColumnPropsExtended[];

const controller = new GridController<WfBase>(
  "WorkflowGrid",
  columns,
  emit,
  props.pageSize
);

controller.initOptions();

const onUrgentClick = function (dataItem: WfSubmissionProductAndIngredient) {
  const wf = dataItem as WfSubmissionProductAndIngredient;

  const errorNotification = {
    message: `Error marking Job ${wf.JobId} as ${
      wf.IsUrgent ? "not urgent" : "urgent"
    }. Please try again`,
    type: NotificationTypeEnum.error,
    duration: 5000,
  };

  handleResponsePromiseAsync(
    api.get("SetWorkflowUrgentStatus", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      workflowId: wf.id,
      isUrgent: !wf.IsUrgent,
    }),
    (data) => {
      if (data) {
        wf.IsUrgent = !wf.IsUrgent;
        notificationStore.notify({
          message: `Job ${wf.JobId} marked as ${
            wf.IsUrgent ? "urgent" : "not urgent"
          } `,
          type: NotificationTypeEnum.success,
          duration: 5000,
        });
      } else {
        notificationStore.notify(errorNotification);
      }
    },
    "WorkflowGrid",
    "onUrgentClick",
    () => {
      notificationStore.notify(errorNotification);
    }
  );
};

const buildUrl = function (workflow: WfSubmissionProductAndIngredient) {
  let url = "";
  if (
    workflow.id > 0 &&
    (workflow.JobType === EnumSubmissionType.ProductAdd ||
      workflow.JobType === EnumSubmissionType.ProductCopy ||
      workflow.JobType === EnumSubmissionType.ProductTerminate)
  ) {
    url = `WorkflowDetail?workflowId=${workflow.id}&HistoryMode=HideSome`;
  } else if (
    workflow.id > 0 &&
    (workflow.JobType === EnumSubmissionType.IngredientAdd ||
      workflow.JobType === EnumSubmissionType.IngredientDelete)
  ) {
    url = `WorkflowDetailIngredients?JobID=${workflow.JobId}&HistoryMode=HideSome`;
  }
  return url;
};

const onNotesClick = function (dataItem: WfSubmissionProductAndIngredient) {
  const wf = dataItem as WfSubmissionProductAndIngredient;
  emit("privateNotesClicked", wf);
};

const onColumnsSubmit = function (columnsProps: GridColumnProps[]) {
  controller.onColumnsSubmit(columnsProps);
  console.debug("RcRfrContactsGrid.vue, onColumnSubmit", columnsProps);

  emit("columnssubmit", columnsProps);
};

const onColumnReorder = function (e: GridColumnReorderEvent) {
  emit("columnreorder", e);
};

watchEffect(() => {
  controller.skip.value = props.skip;
});

watchEffect(() => {
  controller.take.value = props.pageSize;
});
</script>

<style scoped lang="scss">
.workflow-grid {
  height: 600px;

  .urgent-icon {
    margin: auto;
    cursor: pointer;
  }

  .private-notes-icon {
    margin: auto;
    cursor: pointer;
  }
}

.k-grid {
  a.is-download-link {
    svg {
      height: 12px;
    }

    &:before {
      content: "";
    }
  }

  .k-pager {
    .k-input-inner {
      padding: 0.375rem 0.375rem;
    }
  }
}
</style>
